import * as React from 'react';
import { RateUnit, toHourly } from '@clawhire/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';
import { SectionCard } from './SectionCard';
import { RateUnitToggle } from './RateUnitToggle';
import { parseNumber } from './api';

export interface RateNormaliserCardProps {
  rateText: string | null;
  rateEurH: number | null;
  hoursPerDay: number;
  canEdit: boolean;
  onSave: (patch: {
    rateExpectationEurH: number | null;
    rateExpectationText: string | null;
  }) => Promise<unknown>;
}

/**
 * Rate expectation: free text as the candidate/supplier said it, plus number + unit that is
 * normalised live to EUR/h (`hourly = daily / hours_per_day`, 2 decimals) and saved as
 * `rateExpectationEurH` / `rateExpectationText`.
 */
export function RateNormaliserCard({
  rateText,
  rateEurH,
  hoursPerDay,
  canEdit,
  onSave,
}: RateNormaliserCardProps) {
  const [text, setText] = React.useState(rateText ?? '');
  const [amount, setAmount] = React.useState(rateEurH === null ? '' : String(rateEurH));
  const [unit, setUnit] = React.useState<RateUnit>('HOUR');
  const [saving, setSaving] = React.useState(false);
  const ids = { text: React.useId(), amount: React.useId() };

  React.useEffect(() => {
    setText(rateText ?? '');
    setAmount(rateEurH === null ? '' : String(rateEurH));
    setUnit('HOUR');
  }, [rateText, rateEurH]);

  const parsed = parseNumber(amount);
  const hourly = parsed !== null && parsed >= 0 ? toHourly(parsed, unit, hoursPerDay || 8) : null;
  const dirty = (text.trim() || null) !== (rateText ?? null) || hourly !== rateEurH;

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ rateExpectationEurH: hourly, rateExpectationText: text.trim() || null });
      toast.success('Rate expectation saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the rate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Rate expectation"
      description={`Rates are always stored per hour. Day rates are divided by ${hoursPerDay || 8} hours.`}
      actions={
        canEdit ? (
          <Button size="sm" onClick={() => void save()} disabled={!dirty} loading={saving}>
            Save
          </Button>
        ) : null
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={ids.text}>As stated</Label>
          <Input
            id={ids.text}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. 375 EUR/day via Amber Digital"
            disabled={!canEdit}
            maxLength={200}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={ids.amount}>Amount (EUR)</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id={ids.amount}
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-32 tabular-nums"
              disabled={!canEdit}
            />
            <RateUnitToggle value={unit} onChange={setUnit} disabled={!canEdit} />
          </div>
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium leading-none">Normalised</span>
          <p className="text-2xl font-semibold tabular-nums" aria-live="polite" data-testid="rate-hourly">
            {hourly === null ? (
              <span className="text-muted-foreground">–</span>
            ) : (
              `= ${hourly.toFixed(2)} EUR/h`
            )}
          </p>
          {unit === 'DAY' && parsed !== null ? (
            <p className="text-xs text-muted-foreground">
              {parsed} EUR/day ÷ {hoursPerDay || 8} h
            </p>
          ) : null}
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Stored value: {rateEurH === null ? '–' : `${rateEurH} EUR/h`}
        {rateText ? ` (${rateText})` : ''}
      </p>
    </SectionCard>
  );
}
